/**
 * Conversation Tree Component
 *
 * 顯示 Agent 對話過程的視覺化元件
 * - 支援多輪對話顯示
 * - 依 iteration 分組顯示
 * - 支援展開/收合每個區塊
 * - Thinking 區塊預設收合
 * - Tool Result 支援 JSON 格式化顯示
 * - Raw Request/Response 可展開
 */

import { useState, useEffect, useRef } from 'react';
import type { AgentEvent, TokenUsage } from '../../services/api/agent';

/**
 * 對話輪次結構（與 AgenticTestTab 共用）
 */
interface ConversationRound {
  roundNumber: number;
  userMessage: string;
  agentEvents: AgentEvent[];
  finalResponse?: string;
  totalUsage?: TokenUsage;
  isComplete: boolean;
}

interface ConversationTreeProps {
  events: AgentEvent[];
  isStreaming: boolean;
  /** 歷史對話輪次（多輪對話支援） */
  rounds?: ConversationRound[];
  /** 當前進行中的輪次事件 */
  currentRoundEvents?: AgentEvent[];
}

interface IterationGroup {
  iteration: number;
  events: AgentEvent[];
  rawRequest?: unknown;
  rawResponse?: unknown;
  usage?: TokenUsage;
}

/**
 * 將事件分組為 iteration
 */
function groupEventsByIteration(events: AgentEvent[]): IterationGroup[] {
  const groups: IterationGroup[] = [];
  let currentGroup: IterationGroup | null = null;

  for (const event of events) {
    if (event.type === 'iteration_start') {
      currentGroup = {
        iteration: event.iteration,
        events: [event],
        rawRequest: (event as { rawRequest?: unknown }).rawRequest,
      };
      groups.push(currentGroup);
    } else if (currentGroup) {
      currentGroup.events.push(event);
      if (event.type === 'iteration_end') {
        currentGroup.rawResponse = (event as { rawResponse?: unknown }).rawResponse;
        currentGroup.usage = event.usage;
      }
    }
  }

  return groups;
}

export function ConversationTree({
  events,
  isStreaming,
  rounds = [],
  currentRoundEvents = [],
}: ConversationTreeProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // 自動捲動到底部
  useEffect(() => {
    if (isStreaming && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [events, isStreaming, currentRoundEvents]);

  // 如果有多輪對話，使用新的顯示邏輯
  if (rounds.length > 0 || currentRoundEvents.length > 0) {
    return (
      <div
        ref={containerRef}
        className="space-y-6 max-h-[600px] overflow-y-auto pr-2"
      >
        {/* 歷史輪次 */}
        {rounds.map((round) => (
          <RoundBlock key={round.roundNumber} round={round} />
        ))}

        {/* 當前進行中的輪次 */}
        {currentRoundEvents.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="badge badge-primary">
                第 {rounds.length + 1} 輪
              </span>
              <span className="text-xs text-primary-600 dark:text-primary-400 animate-pulse">
                處理中...
              </span>
            </div>
            {groupEventsByIteration(currentRoundEvents).map((group) => (
              <IterationBlock key={group.iteration} group={group} />
            ))}
          </div>
        )}

        {/* Streaming 指示器 */}
        {isStreaming && (
          <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400">
            <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse" />
            <span className="text-sm">處理中...</span>
          </div>
        )}
      </div>
    );
  }

  // 原有的單輪顯示邏輯（向後相容）
  const groups = groupEventsByIteration(events);
  const completeEvent = events.find((e) => e.type === 'complete');
  const errorEvent = events.find((e) => e.type === 'error');

  if (events.length === 0) {
    return (
      <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
        尚無對話內容
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="space-y-4 max-h-[600px] overflow-y-auto pr-2"
    >
      {groups.map((group) => (
        <IterationBlock key={group.iteration} group={group} />
      ))}

      {/* 完成摘要 */}
      {completeEvent && completeEvent.type === 'complete' && (
        <div className="p-4 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800/50 rounded-card">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-success-700 dark:text-success-300">
              對話完成
            </span>
            <div className="text-sm text-success-600 dark:text-success-400">
              總計: {completeEvent.totalUsage.inputTokens} / {completeEvent.totalUsage.outputTokens} tokens
            </div>
          </div>
          {completeEvent.content && (
            <div className="mt-2 text-sm text-success-700 dark:text-success-300">
              <strong>最終回應:</strong>
              <pre className="mt-1 p-2 bg-success-100 dark:bg-success-900/30 rounded text-xs whitespace-pre-wrap">
                {completeEvent.content.slice(0, 500)}
                {completeEvent.content.length > 500 && '...'}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* 錯誤訊息 */}
      {errorEvent && errorEvent.type === 'error' && (
        <div className="p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800/50 rounded-card">
          <span className="font-medium text-danger-700 dark:text-danger-300">
            發生錯誤: {errorEvent.error}
          </span>
        </div>
      )}

      {/* Streaming 指示器 */}
      {isStreaming && (
        <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400">
          <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse" />
          <span className="text-sm">處理中...</span>
        </div>
      )}
    </div>
  );
}

/**
 * 單一對話輪次區塊
 */
interface RoundBlockProps {
  round: ConversationRound;
}

function RoundBlock({ round }: RoundBlockProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const groups = groupEventsByIteration(round.agentEvents);
  const completeEvent = round.agentEvents.find((e) => e.type === 'complete');

  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-card overflow-hidden">
      {/* 輪次標題 */}
      <div
        className="p-3 bg-neutral-50 dark:bg-neutral-800 flex items-center justify-between cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700/50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <span className="badge badge-primary">第 {round.roundNumber} 輪</span>
          <span className="text-sm text-neutral-700 dark:text-neutral-300 truncate max-w-md">
            {round.userMessage.slice(0, 50)}
            {round.userMessage.length > 50 && '...'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {round.totalUsage && (
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              {round.totalUsage.totalTokens.toLocaleString()} tokens
            </span>
          )}
          <span className="text-neutral-400">{isExpanded ? '▼' : '▶'}</span>
        </div>
      </div>

      {/* 輪次內容 */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* 用戶訊息 */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-button">
            <div className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
              用戶訊息
            </div>
            <p className="text-sm text-blue-800 dark:text-blue-200 whitespace-pre-wrap">
              {round.userMessage}
            </p>
          </div>

          {/* Iteration 區塊 */}
          {groups.map((group) => (
            <IterationBlock key={group.iteration} group={group} />
          ))}

          {/* 完成摘要 */}
          {completeEvent && completeEvent.type === 'complete' && (
            <div className="p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800/50 rounded-button">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-success-700 dark:text-success-300">
                  完成
                </span>
                <div className="text-xs text-success-600 dark:text-success-400">
                  {completeEvent.totalUsage.inputTokens} / {completeEvent.totalUsage.outputTokens} tokens
                </div>
              </div>
              {completeEvent.content && (
                <pre className="text-xs text-success-700 dark:text-success-300 bg-success-100 dark:bg-success-900/30 p-2 rounded whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {completeEvent.content.slice(0, 1000)}
                  {completeEvent.content.length > 1000 && '...'}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * 單一 Iteration 區塊
 */
interface IterationBlockProps {
  group: IterationGroup;
}

function IterationBlock({ group }: IterationBlockProps) {
  const [isRawRequestOpen, setIsRawRequestOpen] = useState(false);
  const [isRawResponseOpen, setIsRawResponseOpen] = useState(false);

  // 收集 text_delta 累積的文字
  const accumulatedText = group.events
    .filter((e) => e.type === 'text_delta')
    .map((e) => (e as { delta: string }).delta)
    .join('');

  // 檢查是否有 tool calls
  const hasToolCalls = group.events.some((e) => e.type === 'tool_call');

  return (
    <div className="card p-4 space-y-3">
      {/* Iteration Header */}
      <div className="flex items-center justify-between pb-2 border-b border-neutral-200 dark:border-neutral-700">
        <div className="flex items-center gap-2">
          <span className="badge badge-neutral">迭代 {group.iteration}</span>
          {hasToolCalls && (
            <span className="badge badge-primary">使用工具</span>
          )}
        </div>
        {group.usage && (
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {group.usage.inputTokens} / {group.usage.outputTokens} tokens
          </span>
        )}
      </div>

      {/* Events */}
      <div className="space-y-2">
        {group.events.map((event, idx) => (
          <EventBlock key={idx} event={event} />
        ))}
      </div>

      {/* Accumulated Text (if any text_delta events) */}
      {accumulatedText && (
        <div className="p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-button">
          <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
            回應文字
          </div>
          <pre className="text-sm text-neutral-700 dark:text-neutral-200 whitespace-pre-wrap">
            {accumulatedText}
          </pre>
        </div>
      )}

      {/* Raw Request/Response */}
      <div className="space-y-1">
        {group.rawRequest !== undefined && group.rawRequest !== null && (
          <details
            open={isRawRequestOpen}
            onToggle={(e) => setIsRawRequestOpen(e.currentTarget.open)}
            className="border border-neutral-200 dark:border-neutral-700 rounded-button overflow-hidden"
          >
            <summary className="px-3 py-2 cursor-pointer text-xs font-medium text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
              Raw Request
            </summary>
            <pre className="p-3 bg-neutral-100 dark:bg-neutral-800 text-xs font-mono text-neutral-600 dark:text-neutral-300 overflow-x-auto max-h-64 overflow-y-auto">
              {JSON.stringify(group.rawRequest, null, 2)}
            </pre>
          </details>
        )}

        {group.rawResponse !== undefined && group.rawResponse !== null && (
          <details
            open={isRawResponseOpen}
            onToggle={(e) => setIsRawResponseOpen(e.currentTarget.open)}
            className="border border-neutral-200 dark:border-neutral-700 rounded-button overflow-hidden"
          >
            <summary className="px-3 py-2 cursor-pointer text-xs font-medium text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
              Raw Response
            </summary>
            <pre className="p-3 bg-neutral-100 dark:bg-neutral-800 text-xs font-mono text-neutral-600 dark:text-neutral-300 overflow-x-auto max-h-64 overflow-y-auto">
              {JSON.stringify(group.rawResponse, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

/**
 * 單一事件區塊
 */
interface EventBlockProps {
  event: AgentEvent;
  /** 巢狀深度（用於 SubAgent 縮排） */
  depth?: number;
}

function EventBlock({ event, depth = 0 }: EventBlockProps) {
  const [isOpen, setIsOpen] = useState(false);

  switch (event.type) {
    case 'thinking':
      return (
        <details
          open={isOpen}
          onToggle={(e) => setIsOpen(e.currentTarget.open)}
          className="bg-amber-50 dark:bg-amber-900/20 rounded-button overflow-hidden"
        >
          <summary className="px-3 py-2 cursor-pointer text-xs font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30">
            Thinking ({event.content.length} chars)
          </summary>
          <pre className="px-3 pb-3 text-xs font-mono text-amber-700 dark:text-amber-200 whitespace-pre-wrap">
            {event.content}
          </pre>
        </details>
      );

    case 'tool_call':
      return (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-button">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
              Tool Call: {event.toolName}
            </span>
            <code className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 rounded text-blue-600 dark:text-blue-300">
              {event.toolId}
            </code>
          </div>
          <pre className="text-xs font-mono text-blue-700 dark:text-blue-200 bg-blue-100 dark:bg-blue-900/30 p-2 rounded overflow-x-auto">
            {JSON.stringify(event.input, null, 2)}
          </pre>
        </div>
      );

    case 'tool_result':
      return (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-button">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-green-700 dark:text-green-300">
              Tool Result: {event.toolName}
            </span>
            <span
              className={`badge ${event.result.success ? 'badge-success' : 'badge-danger'
                }`}
            >
              {event.result.success ? '成功' : '失敗'}
            </span>
          </div>
          <ToolResultContent result={event.result} />
          {/* SubAgent 事件巢狀顯示 */}
          {event.result.subAgentEvents && event.result.subAgentEvents.length > 0 && (
            <SubAgentEventsBlock
              events={event.result.subAgentEvents}
              depth={depth + 1}
            />
          )}
        </div>
      );

    case 'subagent_start':
      return (
        <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-button border-l-2 border-purple-400">
          <div className="flex items-center gap-2">
            <span className="badge badge-purple text-xs">SubAgent</span>
            <span className="text-xs font-medium text-purple-700 dark:text-purple-300">
              {event.taskType}
            </span>
            <code className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/40 rounded text-purple-600 dark:text-purple-300">
              depth: {event.depth}
            </code>
          </div>
        </div>
      );

    case 'subagent_event':
      return (
        <div className="ml-4 border-l-2 border-purple-300 dark:border-purple-700 pl-3">
          <EventBlock event={event.nestedEvent} depth={depth + 1} />
        </div>
      );

    case 'subagent_end':
      return (
        <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-button border-l-2 border-purple-400">
          <div className="flex items-center gap-2 mb-2">
            <span className="badge badge-purple text-xs">SubAgent 完成</span>
            <span
              className={`badge ${event.result.success ? 'badge-success' : 'badge-danger'
                }`}
            >
              {event.result.success ? '成功' : '失敗'}
            </span>
          </div>
          <ToolResultContent result={event.result} />
        </div>
      );

    case 'text_delta':
      // 不單獨顯示 text_delta，由 IterationBlock 統一累積顯示
      return null;

    case 'iteration_start':
    case 'iteration_end':
      // 這些由 header 處理
      return null;

    default:
      return null;
  }
}

/**
 * Tool Result 內容顯示
 */
interface ToolResultContentProps {
  result: {
    success: boolean;
    content: string | Record<string, unknown>;
    error?: string;
  };
}

function ToolResultContent({ result }: ToolResultContentProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (result.error) {
    return (
      <div className="text-xs text-danger-600 dark:text-danger-400">
        錯誤: {result.error}
      </div>
    );
  }

  const content =
    typeof result.content === 'string'
      ? result.content
      : JSON.stringify(result.content, null, 2);

  const isLong = content.length > 500;
  const displayContent = isExpanded ? content : content.slice(0, 500);

  return (
    <div>
      <pre className="text-xs font-mono text-green-700 dark:text-green-200 bg-green-100 dark:bg-green-900/30 p-2 rounded overflow-x-auto whitespace-pre-wrap max-h-64 overflow-y-auto">
        {displayContent}
        {isLong && !isExpanded && '...'}
      </pre>
      {isLong && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-1 text-xs text-green-600 dark:text-green-400 hover:underline"
        >
          {isExpanded ? '收合' : `展開全部 (${content.length} chars)`}
        </button>
      )}
    </div>
  );
}

/**
 * SubAgent 事件區塊
 *
 * 顯示 SubAgent 執行過程中產生的所有事件
 * 支援展開/收合，預設收合
 */
interface SubAgentEventsBlockProps {
  events: AgentEvent[];
  depth: number;
}

function SubAgentEventsBlock({ events, depth }: SubAgentEventsBlockProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // 統計資訊
  const iterationCount = events.filter((e) => e.type === 'iteration_start').length;
  const toolCallCount = events.filter((e) => e.type === 'tool_call').length;
  const hasThinking = events.some((e) => e.type === 'thinking');

  // 找出 SubAgent 任務類型
  const startEvent = events.find((e) => e.type === 'subagent_start');
  const taskType = startEvent && 'taskType' in startEvent ? startEvent.taskType : 'SubAgent';

  return (
    <div className="mt-3 ml-2 border-l-2 border-purple-300 dark:border-purple-700 pl-3">
      <div
        className="flex items-center gap-2 cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 p-2 rounded-button"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="badge badge-purple text-xs">SubAgent</span>
        <span className="text-xs text-purple-700 dark:text-purple-300">
          {taskType}
        </span>
        <span className="text-xs text-neutral-500 dark:text-neutral-400">
          • {iterationCount} iter
          {toolCallCount > 0 && ` • ${toolCallCount} tools`}
          {hasThinking && ' • thinking'}
        </span>
        <span className="text-neutral-400 ml-auto">
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>

      {isExpanded && (
        <div className="mt-2 space-y-2">
          {events.map((event, idx) => (
            <EventBlock key={idx} event={event} depth={depth} />
          ))}
        </div>
      )}
    </div>
  );
}
